import { createContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import * as API from '../../API';
import useProvidedContext from '../../Hooks/UseProvidedContext';
import { createLoadedEntity, EntityLoading, EntityNotLoaded, isEntityLoaded, type Entity } from '@/Models/Entity';
import { type SystemSettingsModel } from '@/Models/SystemSettings';
import SetUniqueIdentifierForWCF from '../../Scripts/WorkAtHomeIdentifier';
import { useAuthenticationContext } from './AuthenticationContext';

interface SystemSettingsContextData {
    settings: Entity<SystemSettingsModel>;
    // Setter functions
    getValue: (key: string) => string | undefined;
    getBoolean: (key: string) => boolean | undefined;
}

const createActions = (entity?: SystemSettingsModel): any => {
    return {
        getValue: (key: string): string | undefined => {
            return entity?.settings[key]?.value;
        },
        getBoolean: (key: string): boolean => {
            return entity?.settings[key]?.value === 'Yes';
        },
    };
};

export const SystemSettingsContext = createContext<SystemSettingsContextData | null>(null);

export function SystemSettingsContextProvider({ children }: Readonly<{ children: ReactNode }>): React.ReactElement {
    const [settings, setSettings] = useState<Entity<SystemSettingsModel>>(EntityNotLoaded.instance);
    const authenticationContext = useAuthenticationContext();

    if (!authenticationContext.isLoggedIn()) {
        throw new Error('Authentication required to get system settings');
    }

    useEffect(() => {
        setSettings(EntityLoading.instance);
        void API.general.getGeneralSettings().then((systemSettings: SystemSettingsModel | null) => {
            if (systemSettings == null) {
                throw new Error('Server returned no data for the system settings');
            }

            const loadedEntity = createLoadedEntity(systemSettings);
            setSettings(loadedEntity);

            SetUniqueIdentifierForWCF();
        });
    }, []);

    const systemSettingsContextValue = useMemo(() => ({ settings, ...createActions(isEntityLoaded(settings) ? settings : undefined) }), [settings]);

    return (
        <SystemSettingsContext.Provider value={systemSettingsContextValue}>
            {children}
        </SystemSettingsContext.Provider>
    );
}

export const useSystemSettingsContext = (): SystemSettingsContextData => {
    return useProvidedContext(SystemSettingsContext);
};
